import React, { useEffect, useState } from "react";
import { Box, Card, CircularProgress, Stack, TableCell, Typography, Avatar } from "@mui/material";
import GenericDataTable from "../GenericDataTable";
import TableLocationValue from "../TableLocationValue";

const API_BASE = (process.env.REACT_APP_API_BASE || "").replace(/\/$/, "");

const getAuthHeaders = () => {
  const token =
    localStorage.getItem("token") ||
    localStorage.getItem("authToken") ||
    localStorage.getItem("jwt") ||
    "";

  return token
    ? {
        Authorization: token.startsWith("Bearer ") ? token : `Bearer ${token}`,
        "x-access-token": token.replace(/^Bearer\s+/i, ""),
      }
    : {};
};

const columns = [
  { id: "full_name", label: "Candidate", width: "25%" },
  { id: "title", label: "Job Title", width: "20%" },
  { id: "location", label: "Location", width: "15%" },
  { id: "status", label: "Status", width: "10%" },
  { id: "stage", label: "Stage", width: "10%" },
  { id: "source", label: "Source", width: "10%" },
  { id: "owner", label: "Owner", width: "10%" },
];

export default function CompanyCandidatesTabContent({ company = {}, leadId, entityType = "lead" }) {
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");

  const resolvedLeadId = Number(
    leadId || company?.lead_id || company?.leadId || company?.entity_id || company?.id || 0
  );
  const resolvedEntityType = String(entityType || company?.entityType || "lead").toLowerCase();

  useEffect(() => {
    if (!resolvedLeadId) {
      setCandidates([]);
      setLoadError("");
      setLoading(false);
      return;
    }

    let active = true;
    const controller = new AbortController();

    setLoading(true);
    setLoadError("");

    const endpoint =
      resolvedEntityType === "lead"
        ? `${API_BASE}/api/leads/${encodeURIComponent(resolvedLeadId)}/candidates`
        : `${API_BASE}/api/companies/${encodeURIComponent(resolvedLeadId)}/candidates`;

    fetch(endpoint, {
      method: "GET",
      headers: {
        ...getAuthHeaders(),
      },
      credentials: "include",
      signal: controller.signal,
    })
      .then(async (response) => {
        if (!response.ok) {
          const errorText = await response.text().catch(() => "");
          throw new Error(errorText || "Failed to load candidates");
        }
        return response.json();
      })
      .then((payload) => {
        if (!active) return;
        const rows = Array.isArray(payload?.rows)
          ? payload.rows
          : Array.isArray(payload?.candidates)
          ? payload.candidates
          : [];
        setCandidates(rows);
      })
      .catch((err) => {
        if (!active || err?.name === "AbortError") return;
        console.error("Failed to load company candidates:", err);
        setLoadError(err?.message || "Failed to load candidates");
      })
      .finally(() => {
        if (!active) return;
        setLoading(false);
      });

    return () => {
      active = false;
      controller.abort();
    };
  }, [resolvedEntityType, resolvedLeadId]);

  const renderRow = (row) => {
    const avatarUrl = row?.avatar_url || row?.profile_image || null;

    // Parse full_name which may contain "Name | Title Email" format
    const rawFullName = String(row?.full_name || row?.name || row?.candidate_name || "").trim();
    let fullName = rawFullName;
    let parsedTitle = "";
    let parsedEmail = "";

    if (rawFullName.includes(" | ")) {
      const parts = rawFullName.split(" | ").map((p) => p.trim()).filter(Boolean);
      fullName = parts[0] || rawFullName;
      const rest = parts.slice(1).join(" ");
      if (rest) {
        const emailMatch = rest.match(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i);
        if (emailMatch) {
          parsedEmail = emailMatch[0];
          parsedTitle = rest.replace(parsedEmail, "").trim();
        } else {
          parsedTitle = rest;
        }
      }
    }

    // Strip candidate name from title if it was accidentally stored there
    let rawTitle = String(row?.title || row?.candidate_title || "").trim();
    if (rawTitle.includes(" | ")) {
      const titleParts = rawTitle.split(" | ").map((p) => p.trim()).filter(Boolean);
      // If the first part matches the candidate name, remove it
      if (titleParts[0] === fullName) {
        rawTitle = titleParts.slice(1).join(" | ").trim();
      }
      // Strip emails from title
      rawTitle = rawTitle.replace(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi, "").trim();
      // Strip URLs that look like domain names (not job titles)
      rawTitle = rawTitle.replace(/\b[a-z0-9.-]+\.[a-z]{2,}\b/gi, "").trim();
    }
    // If rawTitle equals the candidate name, clear it
    if (rawTitle === fullName) rawTitle = "";

    const displayTitle = rawTitle || parsedTitle || "-";
    const displayEmail = row?.email || row?.user_email || parsedEmail || "";

    const initials = (fullName || "-")
      .split(" ")
      .filter(Boolean)
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);

    return (
      <>
        <TableCell>
          <Stack direction="row" spacing={1.5} alignItems="center">
            {avatarUrl ? (
              <Avatar
                src={avatarUrl}
                alt={fullName}
                sx={{ width: 32, height: 32, fontSize: 14 }}
              />
            ) : (
              <Avatar sx={{ width: 32, height: 32, fontSize: 14, bgcolor: "#9C4FFF" }}>
                {initials}
              </Avatar>
            )}
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>{fullName}</Typography>
              {displayEmail && (
                <Typography variant="caption" sx={{ color: "#6B7280", display: "block" }}>
                  {displayEmail}
                </Typography>
              )}
            </Box>
          </Stack>
        </TableCell>
        <TableCell>{displayTitle}</TableCell>
        <TableCell>
          <TableLocationValue row={row} />
        </TableCell>
        <TableCell>{row?.status || row?.candidate_status || "-"}</TableCell>
        <TableCell>{row?.stage || row?.candidate_stage || "-"}</TableCell>
        <TableCell>{row?.source || row?.last_sync_label || "-"}</TableCell>
        <TableCell>{row?.owner || row?.recruiter_name || "-"}</TableCell>
      </>
    );
  };

  return (
    <Box>
      {!resolvedLeadId ? (
        <Typography sx={{ color: "#6B7280" }}>
          No company selected to load candidates.
        </Typography>
      ) : loading ? (
        <Stack direction="row" spacing={1} alignItems="center">
          <CircularProgress size={18} />
          <Typography sx={{ fontWeight: 700 }}>
            {resolvedEntityType === "lead" ? "Loading matched candidates..." : "Loading company candidates..."}
          </Typography>
        </Stack>
      ) : loadError ? (
        <Card sx={{ p: 2, backgroundColor: "#FEF2F2", border: "1px solid #FECACA" }}>
          <Typography sx={{ color: "#B91C1C" }}>{loadError}</Typography>
        </Card>
      ) : candidates.length === 0 ? (
        <Typography sx={{ color: "#6B7280" }}>
          {resolvedEntityType === "lead"
            ? "No AI candidate matches are saved for this lead yet."
            : "No candidates are currently linked to this company."}
        </Typography>
      ) : (
        <GenericDataTable
          columns={columns}
          rows={candidates}
          renderRow={renderRow}
          defaultOrderBy="created_at"
          defaultOrderDirection="desc"
          includeSelectionColumn={false}
          initialRowsPerPage={10}
          showResultsHeader={true}
        />
      )}
    </Box>
  );
}
